// src/pages/Funcionarios.js - VERSÃO CORRIGIDA
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, TextField, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, CircularProgress, Alert, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, MenuItem, FormControl,
  InputLabel, Select, Avatar, LinearProgress
} from '@mui/material';
import {
  People as PeopleIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  GetApp as ExportIcon, PictureAsPdf as PdfIcon, TableChart as ExcelIcon,
  Search as SearchIcon, Refresh as RefreshIcon, Badge as BadgeIcon, Work as WorkIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Funcionarios = () => {
  const [funcionarios, setFuncionarios] = useState([]);
  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportando, setExportando] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busca, setBusca] = useState('');
  const [dialogAberto, setDialogAberto] = useState(false);
  const [funcionarioEditando, setFuncionarioEditando] = useState(null);
  const [dadosFormulario, setDadosFormulario] = useState({ 
    nome: '', 
    cpf: '', 
    setor: '', 
    codigo: '' 
  });
  const [salvando, setSalvando] = useState(false);

  const { api, user } = useAuth();

  const carregarDados = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('🔄 Carregando dados de funcionários...');
      
      const [resFuncionarios, resSetores] = await Promise.all([
        api.listarFuncionarios(),
        api.listarSetores()
      ]);
      
      console.log('📦 Resposta funcionários:', resFuncionarios);
      console.log('📦 Resposta setores:', resSetores);
      
      if (resFuncionarios.success) {
        setFuncionarios(resFuncionarios.dados || []);
        console.log(`✅ ${resFuncionarios.dados?.length || 0} funcionários carregados`);
      } else {
        setError(resFuncionarios.message || 'Erro ao carregar funcionários');
      }
      
      if (resSetores.success) {
        setSetores(resSetores.dados || []);
        console.log(`✅ ${resSetores.dados?.length || 0} setores carregados`);
      } else {
        console.warn('⚠️ Erro ao carregar setores:', resSetores.message);
      }
      
    } catch (err) {
      console.error('❌ Erro ao carregar dados:', err);
      setError(err.message || 'Erro de conexão ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const abrirDialogAdicionar = () => {
    setFuncionarioEditando(null);
    setDadosFormulario({ nome: '', cpf: '', setor: '', codigo: '' });
    setDialogAberto(true);
  };

  const abrirDialogEditar = (funcionario) => {
    console.log('✏️ Editando funcionário:', funcionario);
    setFuncionarioEditando(funcionario);
    setDadosFormulario({ 
      nome: funcionario.nome || '', 
      cpf: funcionario.cpf || '', 
      setor: funcionario.setor || '', 
      codigo: funcionario.codigo || '' 
    });
    setDialogAberto(true);
  };

  const validarFormulario = () => {
    if (!dadosFormulario.nome.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    
    if (!dadosFormulario.cpf.trim()) {
      setError('CPF é obrigatório');
      return false;
    }
    
    if (!dadosFormulario.codigo.trim()) {
      setError('Código é obrigatório');
      return false;
    }
    
    // Validação básica de CPF (11 dígitos)
    const cpfLimpo = dadosFormulario.cpf.replace(/\D/g, '');
    if (cpfLimpo.length !== 11) {
      setError('CPF deve ter exatamente 11 dígitos');
      return false;
    }
    
    return true;
  };

  const salvarFuncionario = async () => {
    // Limpar mensagens anteriores
    setError('');
    setSuccess('');
    
    if (!validarFormulario()) {
      return; // Para aqui se a validação falhar
    }

    setSalvando(true);
    
    try {
      console.log('💾 Salvando funcionário:', dadosFormulario);
      
      let response;
      if (funcionarioEditando) {
        console.log('✏️ Atualizando funcionário ID:', funcionarioEditando.id);
        response = await api.atualizarFuncionario(funcionarioEditando.id, dadosFormulario);
      } else {
        console.log('➕ Criando novo funcionário');
        response = await api.criarFuncionario(dadosFormulario);
      }
      
      console.log('📦 Resposta do servidor:', response);
      
      if (response.success) {
        setSuccess(response.message || 'Funcionário salvo com sucesso!');
        
        // Aguardar um pouco para mostrar a mensagem de sucesso, depois fechar o modal
        setTimeout(() => {
          setDialogAberto(false);
          setSuccess(''); // Limpar a mensagem do modal
          carregarDados(); // Recarregar lista
        }, 2000);
      } else {
        throw new Error(response.message || 'Erro desconhecido');
      }
      
    } catch (err) {
      console.error('❌ Erro ao salvar funcionário:', err);
      setError(err.message || 'Erro ao salvar funcionário');
    } finally {
      setSalvando(false);
    }
  };

  const excluirFuncionario = async (funcionario) => {
    if (!window.confirm(`Tem certeza que deseja excluir ${funcionario.nome}?`)) {
      return;
    }
    
    setError('');
    setSuccess('');
    
    try {
      console.log('🗑️ Excluindo funcionário:', funcionario);
      
      const response = await api.excluirFuncionario(funcionario.id);
      
      if (response.success) {
        setSuccess(response.message || 'Funcionário excluído com sucesso!');
        await carregarDados(); // Recarregar lista
      } else {
        throw new Error(response.message || 'Erro ao excluir funcionário');
      }
      
    } catch (err) {
      console.error('❌ Erro ao excluir funcionário:', err);
      setError(err.message || 'Erro ao excluir funcionário');
    }
  };

  const funcionariosFiltrados = funcionarios.filter(func => 
    (func.nome?.toLowerCase() || '').includes(busca.toLowerCase()) ||
    (func.cpf?.includes(busca)) ||
    (func.codigo?.includes(busca)) ||
    (func.setor && func.setor.toLowerCase().includes(busca.toLowerCase()))
  );

  const formatarCPF = (cpf) => {
    if (!cpf) return '';
    const cleaned = cpf.replace(/\D/g, '');
    if (cleaned.length !== 11) return cpf;
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatarData = (dataString) => {
    if (!dataString) return 'N/A';
    try {
      return new Date(dataString).toLocaleDateString('pt-BR');
    } catch {
      return 'N/A';
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  return (
    <Box sx={{ p: 3, minHeight: '100vh' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ 
        p: 3, mb: 3, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        color: 'white', 
        borderRadius: 2 
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              👥 Gestão de Funcionários
            </Typography>
            <Typography variant="subtitle1" opacity={0.9}>
              Cadastro e controle da equipe
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            {user.nivel >= 3 && (
              <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={abrirDialogAdicionar}
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } 
                }}
              >
                Novo Funcionário
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Busca e Estatísticas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ p: 2, borderRadius: 2 }}>
            <TextField 
              fullWidth 
              placeholder="Buscar por nome, CPF, código ou setor..." 
              value={busca} 
              onChange={(e) => setBusca(e.target.value)} 
              InputProps={{ 
                startAdornment: <SearchIcon sx={{ mr: 1, color: '#64748b' }} /> 
              }} 
            />
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ 
            p: 2, 
            textAlign: 'center', 
            borderRadius: 2, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white' 
          }}>
            <Typography variant="h4" fontWeight="bold">
              {funcionariosFiltrados.length}
            </Typography>
            <Typography variant="body2" opacity={0.9}>
              Funcionários {busca ? 'encontrados' : 'cadastrados'}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Lista de Funcionários */}
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">
              📋 Lista de Funcionários
            </Typography>
            <Box>
              <IconButton 
                onClick={carregarDados} 
                disabled={loading}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
          
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : funcionariosFiltrados.length > 0 ? (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Funcionário</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>CPF</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Setor</TableCell>
                    {user.nivel >= 3 && (
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Ações</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {funcionariosFiltrados.map((funcionario) => (
                    <TableRow key={funcionario.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ 
                            mr: 2, 
                            backgroundColor: '#6366f1', 
                            width: 40, 
                            height: 40 
                          }}>
                            {funcionario.nome.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {funcionario.nome}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ID: {funcionario.id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatarCPF(funcionario.cpf)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={funcionario.codigo} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                          icon={<BadgeIcon />} 
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <WorkIcon sx={{ mr: 1, fontSize: 16, color: '#10b981' }} />
                          {funcionario.setor || 'N/A'}
                        </Box>
                      </TableCell>
                      {user.nivel >= 3 && (
                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            <IconButton 
                              size="small" 
                              onClick={() => abrirDialogEditar(funcionario)} 
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => excluirFuncionario(funcionario)} 
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={6}>
              <PeopleIcon sx={{ fontSize: 60, color: '#e2e8f0', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                {busca ? 'Nenhum funcionário encontrado' : 'Nenhum funcionário cadastrado'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {busca ? 'Tente alterar os termos da busca' : 'Clique em "Novo Funcionário" para começar'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog para Adicionar/Editar Funcionário */}
      <Dialog 
        open={dialogAberto} 
        onClose={() => setDialogAberto(false)} 
        maxWidth="md" 
        fullWidth
        style={{ zIndex: 1300 }} // Garantir que fique na frente
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <PeopleIcon color="primary" />
            <Typography variant="h6">
              {funcionarioEditando ? 'Editar Funcionário' : 'Novo Funcionário'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Mostrar erro dentro do modal */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }} 
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}
          
          {/* Mostrar sucesso dentro do modal */}
          {success && (
            <Alert 
              severity="success" 
              sx={{ mb: 2 }} 
              onClose={() => setSuccess('')}
            >
              {success}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField 
                fullWidth 
                label="Nome Completo *" 
                value={dadosFormulario.nome} 
                onChange={(e) => setDadosFormulario(prev => ({ 
                  ...prev, 
                  nome: e.target.value 
                }))} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                fullWidth 
                label="CPF *" 
                value={dadosFormulario.cpf} 
                onChange={(e) => {
                  // Permitir apenas números e limitar a 11 dígitos
                  const apenasNumeros = e.target.value.replace(/\D/g, '');
                  if (apenasNumeros.length <= 11) {
                    setDadosFormulario(prev => ({ 
                      ...prev, 
                      cpf: apenasNumeros 
                    }));
                  }
                }} 
                inputProps={{ 
                  maxLength: 11,
                  pattern: '[0-9]*'
                }} 
                helperText={`${dadosFormulario.cpf.length}/11 dígitos - Apenas números`}
                placeholder="12345678901"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField 
                fullWidth 
                label="Código *" 
                value={dadosFormulario.codigo} 
                onChange={(e) => setDadosFormulario(prev => ({ 
                  ...prev, 
                  codigo: e.target.value 
                }))} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Setor</InputLabel>
                <Select 
                  value={dadosFormulario.setor} 
                  onChange={(e) => setDadosFormulario(prev => ({ 
                    ...prev, 
                    setor: e.target.value 
                  }))} 
                  label="Setor"
                >
                  <MenuItem value="">Nenhum</MenuItem>
                  {setores.map((setor) => (
                    <MenuItem key={setor.id} value={setor.nome}>
                      {setor.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setDialogAberto(false)} 
            disabled={salvando}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={salvarFuncionario} 
            disabled={salvando}
            startIcon={salvando ? <CircularProgress size={20} /> : null}
          >
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Funcionarios;